import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { useAssistantConversations } from "@/features/assistant/use-assistant-conversations";
import { cn } from "@/lib/utils";

interface Props {
  selectedId: number | null;
  onSelect: (id: number) => void;
  onNew: () => void;
}

export function ConversationList({ selectedId, onSelect, onNew }: Props) {
  const { conversations, isLoading, remove } = useAssistantConversations();

  const handleDelete = async (id: number) => {
    if (!window.confirm("Apagar esta conversa?")) return;
    try {
      await remove(id);
      if (id === selectedId) onNew();
    } catch {
      toast.error("Não foi possível apagar a conversa.");
    }
  };

  return (
    <div
      className="flex h-full flex-col"
      data-testid="assistant-conversation-list"
    >
      <div className="p-2">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={onNew}
          data-testid="assistant-new-conversation"
        >
          <Plus className="mr-1 h-4 w-4" /> Nova conversa
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <p className="p-3 text-sm text-muted-foreground">Carregando…</p>
        )}
        {!isLoading && conversations.length === 0 && (
          <p className="p-3 text-sm text-muted-foreground">
            Nenhuma conversa ainda.
          </p>
        )}
        {conversations.map((c) => (
          <div
            key={c.id}
            className={cn(
              "group flex items-center gap-2 border-b px-3 py-2 text-sm hover:bg-muted",
              selectedId === c.id && "bg-muted",
            )}
            data-testid={`assistant-conversation-row-${c.id}`}
          >
            <button
              type="button"
              className="flex-1 truncate text-left"
              onClick={() => onSelect(c.id)}
              data-testid={`assistant-conversation-${c.id}`}
            >
              {c.title ?? "Conversa sem título"}
            </button>
            <Button
              size="icon"
              variant="ghost"
              className="h-6 w-6 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 [@media(pointer:coarse)]:opacity-100"
              aria-label="Apagar conversa"
              onClick={() => handleDelete(c.id)}
              data-testid={`assistant-conversation-delete-${c.id}`}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
